/* eslint-disable @typescript-eslint/no-explicit-any */
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { OidcCallbackHandler, OidcLoginRedirect } from '@nohara-dxdev/react-oidc';
import { LazyLayout, LoadingScreen } from '@nohara-dxdev/react-core';

import OrgRoot, { ErrorBoundary as OrgErrorBoundary } from './routes/org/root';
import AppRoot, { ErrorBoundary as AppErrorBoundary } from './routes/root';
import HomePage from './routes/home';
import { PlansRoute } from './routes/plans';
import {
  PropertiesRoute,
  PropertiesRegisterRoute,
  PropertiesDetailRoute,
} from './routes/properties';
import {
  CompaniesRoute,
  CompaniesRegisterRoute,
  CustomerDetailRoute,
  SupplierDetailRoute,
  ConstructionCompanyDetailRoute,
  WitnessCompanyDetailRoute,
} from './routes/companies';
import { ReportsRoute } from './routes/reports';
import { MembersRoute } from './routes/members';
import NotFoundRoute from './routes/not-found';
import MembersRegisterRoute from './routes/members/register';
import MembersDetailRoute from './routes/members/detail';
import NotAuthorizedRoute from './routes/not-authorized';
import PlanRegisterRoute from './routes/plans/register';
import PlanDetailRoute from './routes/plans/detail';

import { paths } from '@/config/paths';

const convert = (queryClient: QueryClient) => (m: any) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m;
  return {
    ...rest,
    loader: clientLoader?.(queryClient),
    action: clientAction?.(queryClient),
    Component,
  };
};

export const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      path: paths.auth.callback.path,
      element: (
        <OidcCallbackHandler
          successPath={paths.plans.path}
          errorPath={paths.error.path}
          element={<LoadingScreen />}
        />
      ),
    },
    {
      path: paths.auth.login.path,
      element: <OidcLoginRedirect element={<LoadingScreen />} />,
    },
    {
      path: paths.auth.logout.path,
      element: <OidcLoginRedirect element={<LoadingScreen />} />,
    },
    {
      path: paths.homePage.path,
      element: <HomePage />,
    },
    {
      path: paths.notAuthorized.path,
      element: <NotAuthorizedRoute />,
    },
    {
      path: '/',
      element: <AppRoot />,
      ErrorBoundary: AppErrorBoundary,
      children: [
        {
          path: '',
          element: <Navigate to={paths.plans.path} />,
        },
        {
          path: 'plans',
          element: <PlansRoute />,
        },
        {
          path: 'plans/register',
          element: <PlanRegisterRoute />,
        },
        {
          path: 'plans/:planId',
          element: <PlanDetailRoute />,
        },
        {
          path: 'properties',
          element: <PropertiesRoute />,
        },
        {
          path: 'properties/register',
          element: <PropertiesRegisterRoute />,
        },
        {
          path: 'properties/:propertyId',
          element: <PropertiesDetailRoute />,
        },
        {
          path: 'companies',
          element: <CompaniesRoute />,
        },
        {
          path: 'reports',
          element: <ReportsRoute />,
        },
        {
          path: 'members',
          element: <MembersRoute />,
        },
        {
          path: 'members/register',
          element: <MembersRegisterRoute />,
        },
        {
          path: 'members/:memberId',
          element: <MembersDetailRoute />,
        },
        {
          path: paths.org.root.path,
          element: <OrgRoot />,
          ErrorBoundary: OrgErrorBoundary,
          children: [
            {
              path: paths.onboarding.path,
              HydrateFallback: LoadingScreen,
              lazy: () => import('./routes/org/onboarding').then(convert(queryClient)),
            },
            {
              path: paths.org.userProfile.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/org/user').then(convert(queryClient)),
            },
            {
              path: paths.org.selectedOrg.path,
              children: [
                {
                  path: paths.org.dashboard.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/dashboard').then(convert(queryClient)),
                },
                {
                  path: paths.org.userProfile.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/user').then(convert(queryClient)),
                },
                {
                  path: paths.org.users.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/users').then(convert(queryClient)),
                },
                {
                  path: paths.org.roles.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/roles').then(convert(queryClient)),
                },
                {
                  path: paths.org.user.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/user').then(convert(queryClient)),
                },
                {
                  path: paths.org.orgProfile.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/org').then(convert(queryClient)),
                },
              ],
            },
          ],
        },
        {
          path: '/companies/register',
          element: <CompaniesRegisterRoute />,
        },
        {
          path: '/companies/customer/:customerId',
          element: <CustomerDetailRoute />,
        },
        {
          path: '/companies/supplier/:supplierId',
          element: <SupplierDetailRoute />,
        },
        {
          path: '/companies/construction-company/:constructionCompanyId',
          element: <ConstructionCompanyDetailRoute />,
        },
        {
          path: '/companies/witness-company/:witnessCompanyId',
          element: <WitnessCompanyDetailRoute />,
        },
      ],
    },
    {
      path: '*',
      HydrateFallback: LoadingScreen,
      lazy: () => import('./routes/not-found').then(convert(queryClient)),
      element: <NotFoundRoute />,
    },
  ]);

export const AppRouter = () => {
  const queryClient = useQueryClient();

  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  return <RouterProvider router={router} />;
};
